import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { cn } from '../../utils/helpers';

interface RejectReasonModalProps {
  isOpen: boolean;
  title?: string;
  description?: string;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  confirmLabel?: string;
  confirmLoadingLabel?: string;
  confirmVariant?: 'destructive' | 'warning';
  itemCount?: number;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  loading?: boolean;
}

const RejectReasonModal: React.FC<RejectReasonModalProps> = ({
  isOpen,
  title,
  description,
  reasonLabel,
  reasonPlaceholder,
  confirmLabel,
  confirmLoadingLabel,
  confirmVariant = 'destructive',
  itemCount = 1,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const modalTitle = title || (itemCount > 1 ? `Reject ${itemCount} Items` : 'Reject Content');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(reason);
    setReason('');
  };

  const handleCancel = () => {
    setReason('');
    onCancel();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleCancel} />

      {/* Modal */}
      <div className="relative w-full max-w-md mx-4 bg-card border border-border rounded-xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <AlertTriangle className={cn('h-5 w-5', confirmVariant === 'warning' ? 'text-yellow-400' : 'text-destructive')} />
            <h3 className="text-lg font-semibold text-foreground">{modalTitle}</h3>
          </div>
          <button
            onClick={handleCancel}
            className="p-1 rounded-lg hover:bg-muted/50 transition-colors"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            {description || (itemCount > 1
              ? `You are about to reject ${itemCount} content items. They will no longer be publicly visible.`
              : 'This content will be marked as rejected and will not be publicly visible.')}
          </p>

          <div>
            <label
              htmlFor="reject-reason"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              {reasonLabel || 'Rejection Reason'} <span className="text-muted-foreground">(optional)</span>
            </label>
            <textarea
              id="reject-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder={reasonPlaceholder || 'Explain why this content is being rejected...'}
              className={cn(
                'w-full rounded-lg border border-border bg-background px-3 py-2',
                'text-sm text-foreground placeholder:text-muted-foreground',
                'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary',
                'resize-none'
              )}
            />
            <p className="text-xs text-muted-foreground mt-1 text-right">
              {reason.length}/500
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium',
                'border border-border text-foreground',
                'hover:bg-muted/50 transition-colors',
                'disabled:opacity-50'
              )}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium',
                'disabled:opacity-50 transition-colors',
                confirmVariant === 'warning'
                  ? 'bg-yellow-600 text-white hover:bg-yellow-500'
                  : 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {loading
                ? (confirmLoadingLabel || 'Rejecting...')
                : (confirmLabel || 'Reject')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RejectReasonModal;
